import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  TStaffDetailsResponse,
  TStaffListApiResponse,
  TStaffResponse,
} from '../../../../apis/staff/staff.interfaces';
import { useStaffListHook } from '../hooks/useStaffListHook';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';

type TStaffState = {
  errorMessage?: string;
  isLoading: boolean;
  paginationParams: TPaginationParams;
  totalCount: number;
  allStaff: TStaffResponse[];
  filteredStaff: TStaffResponse[];
  simplifiedStaffObject: Record<string, any>;
  selectedStaffId: string | null;
  selectedStaff: TStaffDetailsResponse | null;
};

enum ACTION_TYPE {
  SET_STAFF = 'SET_STAFF',
  SET_FILTERED_STAFF = 'SET_FILTERED_STAFF',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_PAGINATION_PARAMS = 'SET_PAGINATION_PARAMS',
  SET_SELECTED_STAFF_ID = 'SET_SELECTED_STAFF_ID',
  SET_SELECTED_STAFF = 'SET_SELECTED_STAFF',
}

type Action =
  | { type: ACTION_TYPE.SET_STAFF; payload: TStaffListApiResponse }
  | { type: ACTION_TYPE.SET_FILTERED_STAFF; payload: TStaffResponse[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_PAGINATION_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.SET_SELECTED_STAFF_ID; payload: string | null }
  | {
      type: ACTION_TYPE.SET_SELECTED_STAFF;
      payload: TStaffDetailsResponse | null;
    };

const initialState: TStaffState = {
  allStaff: [],
  filteredStaff: [],
  selectedStaffId: null,
  selectedStaff: null,
  errorMessage: undefined,
  isLoading: false,
  simplifiedStaffObject: {},
  paginationParams: {
    page: 1,
    limit: 10,
  },
  totalCount: 0,
};

const StaffContext = createContext<TStaffState | undefined>(undefined);
const StaffDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const staffReducer = (state: TStaffState, action: Action): TStaffState => {
  switch (action.type) {
    case ACTION_TYPE.SET_STAFF:
      return {
        ...state,
        allStaff: action.payload.data,
        filteredStaff: action.payload.data,
        totalCount: action.payload.total,
      };
    case ACTION_TYPE.SET_FILTERED_STAFF:
      return {
        ...state,
        filteredStaff: action.payload,
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_PAGINATION_PARAMS:
      return { ...state, paginationParams: action.payload };
    case ACTION_TYPE.SET_SELECTED_STAFF_ID:
      return { ...state, selectedStaffId: action.payload };
    case ACTION_TYPE.SET_SELECTED_STAFF:
      return { ...state, selectedStaff: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StaffProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(staffReducer, initialState);

  const { data: staffListResponse, isLoading } = useStaffListHook(
    state.paginationParams
  );

  const filteredStaff = useMemo(() => {
    if (state.allStaff.length === 0) return [];
    return state.allStaff;
  }, [state.allStaff]);

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    if (staffListResponse) {
      dispatch({
        type: ACTION_TYPE.SET_STAFF,
        payload: staffListResponse as TStaffListApiResponse,
      });
    }
  }, [staffListResponse]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_FILTERED_STAFF,
      payload: filteredStaff,
    });
  }, [filteredStaff]);

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_STAFF_ID, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_STAFF, payload: null });
  }, [state.paginationParams]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading,
    }),
    [state, isLoading]
  );

  return (
    <StaffContext.Provider value={value}>
      <StaffDispatchContext.Provider value={dispatch}>
        {children}
      </StaffDispatchContext.Provider>
    </StaffContext.Provider>
  );
};

export const useStaffState = () => {
  const context = useContext(StaffContext);
  if (context === undefined) {
    throw new Error('useStaffState must be used within a StaffProvider');
  }
  return context;
};

export const useStaffActions = () => {
  const dispatch = useContext(StaffDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useStaffActions must be used within a StaffProvider');
  }
  const updatePaginationParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_PAGINATION_PARAMS, payload: params });
  };

  const setSelectedStaffId = (id: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_STAFF_ID, payload: id });
  };

  const setSelectedStaff = (staff: TStaffDetailsResponse | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_STAFF, payload: staff });
  };

  return { updatePaginationParams, setSelectedStaffId, setSelectedStaff };
};
